/**
 * Form to API Payload Transformer
 *
 * Transforms frontend form data into the backend API payload structure.
 * Uses centralized scenario configuration from AuthenticationFields.
 *
 * @module ScannerConfigurationForm/utils/transformFormToPayload
 */

import {
  getBackendType,
  getAuthScheme,
  getTargetSystemId,
  transformAuthParams
} from '../AuthenticationFields/scenarioConfig';

/**
 * Transforms frontend form data into the backend API payload structure.
 *
 * @param {Object} formData - The current form state
 * @param {string} formData.name - Scanner name
 * @param {string} [formData.description] - Scanner description
 * @param {Object} formData.parameters - Auth parameters (target, method, credentials)
 * @param {Object} formData.runPolicy - Run policy configuration (times in UTC for backend)
 * @param {boolean} [formData.emailNotificationsEnabled] - Email notifications enabled flag
 * @param {Array<string>} [formData.recipientEmails] - List of recipient email addresses
 *
 * @param {Object} context - Additional context
 * @param {string} context.ownerId - Owner ID
 * @param {string} context.businessGroup - Business Group ID
 * @param {string} [context.orgId] - Organization ID (optional)
 * @param {string} [context.connectionId] - Connection ID (required for edit mode)
 *
 * @returns {Object} The transformed API payload
 *
 * @example
 * const payload = transformFormToPayload(formData, {
 *   ownerId: 'user-uuid',
 *   businessGroup: 'org-uuid'
 * });
 */
export function transformFormToPayload(formData, context) {
  const {
    name,
    description = '',
    parameters,
    runPolicy,
    importMode = 'auto-resolve',
    emailNotificationsEnabled = false,
    recipientEmails = []
  } = formData;
  const { ownerId, businessGroup, orgId, connectionId } = context;

  const { target, method } = parameters;

  // Get targetSystemId from scenario config based on target + auth method
  const targetSystemId = getTargetSystemId(target, method, context);

  const payload = {
    // Top level fields
    name,
    description,
    schedule: JSON.stringify(runPolicy),
    importMode,
    parameters: '{}',
    runPolicy: '{}',

    // IDs
    businessGroup,
    ownerId,

    // Email notifications
    notificationEnabled: emailNotificationsEnabled,

    // Connection
    connection: {
      targetSystemId,
      type: getBackendType(target, method, context),
      authScheme: getAuthScheme(target, method, context),
      authParameters: JSON.stringify(
        transformAuthParams(target, method, parameters, context)
      )
    }
  };

  // Only include orgId if provided
  if (orgId) {
    payload.orgId = orgId;
    payload.connection.orgId = orgId;
  }

  // Include connection.id for edit mode
  if (connectionId) {
    payload.connection.id = connectionId;
  }

  // Include emails if email notifications are enabled and there are recipients
  if (emailNotificationsEnabled && recipientEmails.length > 0) {
    payload.emails = JSON.stringify(recipientEmails);
  }

  return payload;
}

export default transformFormToPayload;
